import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FileText, MessageCircle, DollarSign, LogOut, Home, Calendar, Key, Menu, BookOpen, UserPlus,
} from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/fqevhgzz_Elderra%20logo%20new%20original%20%281%29.png';

const NAV = [
  { to: '/admin', icon: Home, label: 'Dashboard', match: (p) => p === '/admin' },
  { to: '/admin/appointments', icon: Calendar, label: 'Appointments', match: (p) => p.startsWith('/admin/appointments') },
  { to: '/admin/bookings', icon: BookOpen, label: 'All Bookings', match: (p) => p.startsWith('/admin/bookings') },
  { to: '/admin/leads', icon: UserPlus, label: 'Leads', match: (p) => p.startsWith('/admin/leads') },
  { to: '/admin/content', icon: FileText, label: 'Content', match: (p) => p.startsWith('/admin/content') },
  { to: '/admin/campaigns', icon: DollarSign, label: 'Campaigns', match: (p) => p.startsWith('/admin/campaigns') },
  { to: '/admin/whatsapp-groups', icon: MessageCircle, label: 'Groups', match: (p) => p.startsWith('/admin/whatsapp-groups') },
];

const AdminShell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const Sidebar = (
    <aside
      data-testid="admin-sidebar"
      style={{
        width: 240, background: '#0f172a', color: '#fff',
        display: 'flex', flexDirection: 'column',
        padding: '20px 14px', boxSizing: 'border-box',
        height: '100vh', position: 'sticky', top: 0, flexShrink: 0,
        fontFamily: 'Manrope, system-ui, -apple-system, sans-serif',
      }}
      className="adm-sidebar-root"
    >
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 14 }}>
        <div style={{ width: 36, height: 36, background: '#fff', borderRadius: 8, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={LOGO_URL} alt="Elderra" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.01em' }}>elderra.io</div>
          <div style={{ fontSize: 10, opacity: 0.5, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Admin</div>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = item.match(location.pathname);
          return (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 11,
                padding: '10px 12px', borderRadius: 9,
                textDecoration: 'none', fontSize: 13.5, fontWeight: 600,
                color: active ? '#fff' : 'rgba(255,255,255,0.65)',
                background: active ? 'rgba(229,38,106,0.18)' : 'transparent',
                border: active ? '1px solid rgba(229,38,106,0.35)' : '1px solid transparent',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; } }}
            >
              <Icon size={17} /> {item.label}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Link
          to="/admin/change-password"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-nav-password"
          style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 9, textDecoration: 'none', fontSize: 13.5, fontWeight: 600, color: 'rgba(255,255,255,0.65)', transition: 'all 0.15s' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
        >
          <Key size={17} /> Change Password
        </Link>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 9, fontSize: 13.5, fontWeight: 600, color: '#fca5a5', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', fontFamily: 'inherit' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <LogOut size={17} /> Logout
        </button>
      </div>
    </aside>
  );

  return (
    <div
      data-testid="admin-shell"
      style={{ display: 'flex', minHeight: '100vh', background: '#f7f5f2', fontFamily: 'Manrope, system-ui, -apple-system, sans-serif' }}
    >
      {/* Desktop sidebar */}
      <div className="adm-shell-desk-side">{Sidebar}</div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 98 }}
          />
          <div style={{ position: 'fixed', top: 0, left: 0, zIndex: 99 }} className="adm-shell-mobile-side">
            {Sidebar}
          </div>
        </>
      )}

      {/* Main */}
      <main style={{ flex: 1, minWidth: 0, padding: '28px 32px 60px' }} className="adm-shell-main">
        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="admin-mobile-menu-btn"
          className="adm-shell-hamburger"
          style={{ display: 'none', background: '#fff', border: '1px solid #e5e7eb', padding: 9, borderRadius: 10, marginBottom: 12, cursor: 'pointer' }}
        >
          <Menu size={20} />
        </button>
        {children}
      </main>

      <style>{`
        @media (max-width: 900px) {
          .adm-shell-desk-side { display: none !important; }
          .adm-shell-hamburger { display: inline-flex !important; }
          .adm-shell-main { padding: 16px !important; }
        }
        @media (min-width: 901px) {
          .adm-shell-mobile-side { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default AdminShell;
